import getEnv from '../utils/getEnv';
import isJestRunning from '../utils/isJestRunning';

const { REACT_APP_STAGE } = getEnv();

export type LocalAuth0CacheOptions = {
  useRefreshTokens?: boolean;
  cacheLocation?: 'localstorage';
};

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * Auth0 no longer allows silent authentication via the hidden-iframe /
 * session-cookie flow on localhost (the cookie is treated as a blocked
 * third-party cookie), so token renewal fails locally and login loops.
 * Auth0's documented local-development workaround is rotating refresh
 * tokens cached in localstorage.
 *
 * These options must stay local-only: persisting refresh tokens in
 * localstorage is an XSS-exfiltration risk we do not want in deployed
 * environments. Both gates must hold — the bundle must be built with the
 * local stage AND the page must actually be served from localhost — so a
 * misconfigured deployed build can never enable this path.
 *
 * The path is also disabled under Jest: jsdom runs on a localhost URL and
 * getEnv loads .env.development.local there, so without this guard a
 * developer with REACT_APP_STAGE=local would get different Auth0 client
 * arguments in tests than CI does.
 */
export const computeLocalAuth0CacheOptions = (
  stage: string,
  hostname: string | undefined,
  isJest: boolean
): LocalAuth0CacheOptions =>
  !isJest &&
  stage === 'local' &&
  hostname !== undefined &&
  LOCAL_HOSTNAMES.includes(hostname)
    ? { useRefreshTokens: true, cacheLocation: 'localstorage' }
    : {};

const localAuth0CacheOptions = computeLocalAuth0CacheOptions(
  REACT_APP_STAGE,
  typeof window === 'undefined' ? undefined : window.location.hostname,
  isJestRunning()
);

export default localAuth0CacheOptions;
